<script setup lang="ts">

import { ref, computed, onBeforeMount, onBeforeUnmount } from "vue"
import { useStore } from "vuex"
import { useRouter } from "vue-router"
import firebase from "@/firebase"
import { useGetDocs, useGetFilteredDocs, postData, updateData } from "@/mixins/modularFirebaseOperations"
import { formatUser, modalClosed } from "@/mixins/visualFormatters"
import { getQuantityLabel, createBatch, requiresWholeUnits, addBatchTransaction, removeStockTransaction } from "@/composables/inventoryHelpers"
import resetBackground from "@/mixins/resetBackground"
import removeBackground from "@/mixins/removeBackground"
import AcMediumModal from "@/components/reusable/teleports/acMediumModal.vue"
import InventoryItemForm from "@/components/reusable/form-widgets/inventoryItemForm.vue"
import InventoryBatchForm from "@/components/reusable/form-widgets/inventoryBatchForm.vue"
import type { AcInventoryItem, AcInventoryBatch, AcInventoryUpdate, AcInventoryWorksite } from "@/types/inventory"

const COLLECTION = "inventory/items/list"

const router = useRouter()
const store = useStore()
const userProfile = computed(() => store.getters["user/userProfile"])
const formattedUser = computed(() => formatUser(userProfile.value))

const { docs, isLoadingDocs } = useGetFilteredDocs<AcInventoryItem>(COLLECTION, { status: "ACTIVE" })
const items = docs as unknown as import("vue").Ref<(AcInventoryItem & { id: string })[]>

// Active worksites offered as removal destinations.
const { docs: worksites } = useGetDocs<AcInventoryWorksite>("inventory/worksites/list")
const activeWorksites = computed(() => worksites.value.filter((w) => w.isActive))

// ---- Sorting (defaults to alphabetical by product name) ----
type SortKey = "productName" | "brand" | "category"
const sortKey = ref<SortKey>("productName")
const sortDir = ref<"asc" | "desc">("asc")

const sortValue = (item: AcInventoryItem, key: SortKey) => {
    if (key === "brand") return item.productBrand?.name ?? ""
    if (key === "category") return item.productCategory?.name ?? ""
    return item.productName ?? ""
}

const toggleSort = (key: SortKey) => {
    if (sortKey.value === key) {
        sortDir.value = sortDir.value === "asc" ? "desc" : "asc"
    } else {
        sortKey.value = key
        sortDir.value = "asc"
    }
}

const sortIndicator = (key: SortKey) => {
    if (sortKey.value !== key) return ""
    return sortDir.value === "asc" ? "▲" : "▼"
}

const visibleItems = computed(() => {
    const filtered = items.value.filter((item) => item.status === "ACTIVE")
    const dir = sortDir.value === "asc" ? 1 : -1
    return [...filtered].sort((a, b) =>
        dir * sortValue(a, sortKey.value).localeCompare(sortValue(b, sortKey.value), "tr", { sensitivity: "base" })
    )
})

// ---- Archived items (lazy-loaded restore view) ----
const showArchived = ref(false)
const archivedItems = ref<(AcInventoryItem & { id: string })[]>([])
const isLoadingArchived = ref(false)
const archivedLoaded = ref(false)

const loadArchivedItems = async () => {
    if (archivedLoaded.value) return
    isLoadingArchived.value = true
    try {
        const snap = await firebase.firestore().collection(COLLECTION).where("status", "==", "ARCHIVED").get()
        archivedItems.value = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() } as AcInventoryItem & { id: string }))
        archivedLoaded.value = true
    } catch (error) {
        console.error(error)
    } finally {
        isLoadingArchived.value = false
    }
}

const toggleArchived = async () => {
    showArchived.value = !showArchived.value
    if (showArchived.value) await loadArchivedItems()
}

const restoreItem = async (item: AcInventoryItem & { id: string }) => {
    if (!confirm("Bu ürünü geri yüklemek istediğinize emin misiniz?")) return
    await updateData(COLLECTION, item.id, {
        status: "ACTIVE",
        updatedAt: new Date(),
        updatedBy: formattedUser.value,
    })
    archivedItems.value = archivedItems.value.filter((i) => i.id !== item.id)
    items.value.push({ ...item, status: "ACTIVE" })
}

const isAddingItem = ref(false)

// Add-batch modal state
const batchModalItem = ref<(AcInventoryItem & { id: string }) | null>(null)
// Remove (FIFO consume) modal state
const removeModalItem = ref<(AcInventoryItem & { id: string }) | null>(null)
const removeModalAmount = ref<number | null>(null)
const removeModalWorksiteId = ref<string>("")

const handleItemAdded = async (item: Partial<AcInventoryItem>) => {
    try {
        // The first purchase batch is seeded from the form's quantity + unit price + condition.
        const firstBatch = createBatch({
            quantity: item.quantity as number,
            unitPrice: item.originalPrice as number,
            purchaseCurrency: (item.purchaseCurrency ?? "TRY") as NonNullable<AcInventoryBatch["purchaseCurrency"]>,
            condition: (item.condition ?? "NEW") as NonNullable<AcInventoryBatch["condition"]>,
            addedBy: formattedUser.value,
        })

        const finalItem: Partial<AcInventoryItem> = {
            ...item,
            batches: [firstBatch],
            quantity: firstBatch.quantity,
            status: "ACTIVE",
            addedBy: formattedUser.value,
            updatedBy: formattedUser.value,
            createdAt: new Date(),
            updatedAt: new Date(),
            updates: [],
            comments: [],
        }
        const newId = await postData(finalItem, COLLECTION)
        items.value.push({ ...(finalItem as AcInventoryItem), id: newId as string })
    } catch (error) {
        console.error(error)
    } finally {
        isAddingItem.value = false
        modalClosed()
    }
}

// Sync the local (one-time fetched) item with the state persisted by a transaction.
const applyMovementResult = (
    item: AcInventoryItem & { id: string },
    result: { batches: AcInventoryItem["batches"], quantity: number, update: AcInventoryUpdate }
) => {
    item.batches = result.batches
    item.quantity = result.quantity
    if (!item.updates) item.updates = []
    item.updates.push(result.update)
}

// ---- Add stock (new batch) ----
const openBatchModal = (item: AcInventoryItem & { id: string }) => {
    batchModalItem.value = item
}
const closeBatchModal = () => {
    batchModalItem.value = null
    modalClosed()
}
const handleBatchSubmitted = async (payload: any) => {
    const item = batchModalItem.value
    if (!item) return
    const batch = createBatch({ ...payload, addedBy: formattedUser.value })
    try {
        const result = await addBatchTransaction(item.id, batch, formattedUser.value, item.quantityType)
        applyMovementResult(item, result)
    } catch (error: any) {
        alert(error?.message ?? `Bir hata oluştu: ${error}`)
        return
    }
    closeBatchModal()
}

// ---- Remove stock (FIFO consume) ----
const openRemoveModal = (item: AcInventoryItem & { id: string }) => {
    removeModalItem.value = item
    removeModalAmount.value = null
    removeModalWorksiteId.value = ""
}
const closeRemoveModal = () => {
    removeModalItem.value = null
    removeModalAmount.value = null
    removeModalWorksiteId.value = ""
    modalClosed()
}
const confirmRemove = async () => {
    const item = removeModalItem.value
    const amount = Number(removeModalAmount.value)
    if (!item) return
    const label = getQuantityLabel(item.quantityType)
    if (isNaN(amount) || amount <= 0) {
        alert("Lütfen geçerli bir miktar giriniz.")
        return
    }
    if (requiresWholeUnits(item.quantityType) && !Number.isInteger(amount)) {
        alert("Bu ürün adet ile takip edilir; lütfen tam sayı giriniz.")
        return
    }
    if (amount > item.quantity) {
        alert(`Mevcut stoktan (${item.quantity} ${label}) daha fazlasını çıkaramazsınız.`)
        return
    }
    const worksite = activeWorksites.value.find((w) => w.id === removeModalWorksiteId.value) ?? null
    try {
        const result = await removeStockTransaction(item.id, amount, formattedUser.value, item.quantityType, worksite)
        applyMovementResult(item, result)
    } catch (error: any) {
        alert(error?.message ?? `Bir hata oluştu: ${error}`)
        return
    }
    closeRemoveModal()
}

const goToDetail = (item: AcInventoryItem & { id: string }) => {
    router.push(`/inventory/item/${item.id}`)
}

// True when the item's stock has reached or fallen below its renewal threshold.
const isBelowThreshold = (item: AcInventoryItem) =>
    item.renewThreshold != null && item.renewThreshold > 0 && item.quantity <= item.renewThreshold

onBeforeMount(() => {
    removeBackground.methods.removeBackground()
})

onBeforeUnmount(() => {
    resetBackground.methods.resetBackground()
})

</script>

<template>
    <main class="main-container mt-8 content-start items-start">
        <div class="flex col-start-1 col-end-13 justify-between items-center w-full">
            <h2 class="ac-large-title mb-0">Stok Yönetimi</h2>
            <div class="flex items-center gap-6">
                <label class="flex items-center gap-2 text-xl cursor-pointer">
                    <input type="checkbox" :checked="showArchived" @change="toggleArchived">
                    Arşivlenenleri göster
                </label>
                <button type="button" @click="isAddingItem = true" class="ac-add-button">
                    <img class="mr-4 filter-main" width="20" src="../../assets/icons/add.png" alt="">
                    Yeni Ürün Ekle
                </button>
            </div>
        </div>

        <!-- Desktop table -->
        <table v-if="!isLoadingDocs" class="alt-table col-start-1 col-end-13 w-full mt-5 hidden md:table">
            <thead>
                <tr>
                    <th class="sortable-th" @click="toggleSort('productName')">
                        Ürün <span class="sort-indicator">{{ sortIndicator('productName') }}</span>
                    </th>
                    <th class="sortable-th" @click="toggleSort('brand')">
                        Marka <span class="sort-indicator">{{ sortIndicator('brand') }}</span>
                    </th>
                    <th class="sortable-th" @click="toggleSort('category')">
                        Kategori <span class="sort-indicator">{{ sortIndicator('category') }}</span>
                    </th>
                    <th>Miktar</th>
                    <th>Konum</th>
                    <th class="text-right">İşlemler</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="item in visibleItems" :key="item.id" class="hover:bg-gray-100 cursor-pointer" @click="goToDetail(item)">
                    <td class="text-overflow font-medium">
                        <span class="inline-flex items-center gap-2">
                            <span v-if="isBelowThreshold(item)" title="Yenileme eşiği aşıldı">⚠️</span>
                            {{ item.productName }}
                        </span>
                    </td>
                    <td class="text-overflow">{{ item.productBrand?.name || "-" }}</td>
                    <td class="text-overflow">{{ item.productCategory?.name || "-" }}</td>
                    <td>
                        <span v-if="item.quantity === 0" class="font-semibold text-red-600">Stokta yok</span>
                        <span v-else>{{ item.quantity }} {{ getQuantityLabel(item.quantityType) }}</span>
                    </td>
                    <td class="text-overflow">{{ item.location?.name || "-" }}</td>
                    <td>
                        <div class="flex items-center justify-end gap-2" @click.stop>
                            <button type="button" class="icon-action-button" title="Detayları Görüntüle" @click="goToDetail(item)">
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                                    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" />
                                </svg>
                            </button>
                            <button type="button" class="icon-action-button icon-action-button--add" title="Stok Ekle (Yeni Parti)" @click="openBatchModal(item)">
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                                    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                                </svg>
                            </button>
                            <button type="button" class="icon-action-button icon-action-button--remove" title="Stok Çıkar" @click="openRemoveModal(item)">
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                </svg>
                            </button>
                        </div>
                    </td>
                </tr>
                <tr v-if="visibleItems.length === 0">
                    <td colspan="6" class="text-center text-gray-400 py-8">Henüz ürün eklenmemiş.</td>
                </tr>
            </tbody>
        </table>

        <!-- Mobile cards -->
        <div v-if="!isLoadingDocs" class="col-start-1 col-end-13 w-full mt-5 flex flex-col gap-4 md:hidden">
            <div v-for="item in visibleItems" :key="item.id" class="inventory-card">
                <h3 class="inventory-card__title">
                    <span v-if="isBelowThreshold(item)" title="Yenileme eşiği aşıldı">⚠️</span>
                    {{ item.productName }}
                </h3>
                <div class="inventory-card__row">
                    <span class="inventory-card__label">Marka</span>
                    <span>{{ item.productBrand?.name || "-" }}</span>
                </div>
                <div class="inventory-card__row">
                    <span class="inventory-card__label">Kategori</span>
                    <span>{{ item.productCategory?.name || "-" }}</span>
                </div>
                <div class="inventory-card__row">
                    <span class="inventory-card__label">Miktar</span>
                    <span v-if="item.quantity === 0" class="font-semibold text-red-600">Stokta yok</span>
                    <span v-else>{{ item.quantity }} {{ getQuantityLabel(item.quantityType) }}</span>
                </div>
                <div class="inventory-card__row">
                    <span class="inventory-card__label">Konum</span>
                    <span>{{ item.location?.name || "-" }}</span>
                </div>
                <div class="inventory-card__actions">
                    <button type="button" class="icon-action-button" title="Detayları Görüntüle" @click="goToDetail(item)">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" />
                        </svg>
                    </button>
                    <button type="button" class="icon-action-button icon-action-button--add" title="Stok Ekle (Yeni Parti)" @click="openBatchModal(item)">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                    </button>
                    <button type="button" class="icon-action-button icon-action-button--remove" title="Stok Çıkar" @click="openRemoveModal(item)">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                    </button>
                </div>
            </div>
            <div v-if="visibleItems.length === 0" class="text-center text-gray-400 py-8">Henüz ürün eklenmemiş.</div>
        </div>

        <!-- Archived items (restore view) -->
        <section v-if="showArchived" class="col-start-1 col-end-13 w-full mt-12">
            <h3 class="text-3xl font-semibold mb-4">Arşivlenen Ürünler</h3>
            <p v-if="isLoadingArchived" class="text-gray-500 py-4">Yükleniyor...</p>
            <table v-else-if="archivedItems.length" class="alt-table w-full">
                <thead>
                    <tr>
                        <th>Ürün</th>
                        <th>Marka</th>
                        <th>Kategori</th>
                        <th>Miktar</th>
                        <th class="text-right">İşlemler</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="item in archivedItems" :key="item.id" class="opacity-70">
                        <td class="text-overflow font-medium">{{ item.productName }}</td>
                        <td class="text-overflow">{{ item.productBrand?.name || "-" }}</td>
                        <td class="text-overflow">{{ item.productCategory?.name || "-" }}</td>
                        <td>{{ item.quantity }} {{ getQuantityLabel(item.quantityType) }}</td>
                        <td>
                            <div class="flex items-center justify-end">
                                <button type="button" class="table-button" @click="restoreItem(item)">
                                    Geri Yükle
                                </button>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
            <p v-else class="text-gray-400 py-4">Arşivlenmiş ürün bulunmuyor.</p>
        </section>

        <!-- Add item modal -->
        <teleport to="body">
            <transition
                appear
                type="animation"
                enter-active-class="animate__animated animate__fadeIn animate__fast"
                leave-active-class="animate__animated animate__fadeOut animate__faster"
            >
                <ac-medium-modal class="lg:!top-44 overflow-y-scroll" @modal-closed="isAddingItem = false" v-if="isAddingItem" width="60vw" height="fit-content" title="Yeni Ürün Ekle">
                    <inventory-item-form @item-added="handleItemAdded" @cancel="isAddingItem = false" />
                </ac-medium-modal>
            </transition>
        </teleport>

        <!-- Add stock (new batch) modal -->
        <teleport to="body">
            <transition
                appear
                type="animation"
                enter-active-class="animate__animated animate__fadeIn animate__fast"
                leave-active-class="animate__animated animate__fadeOut animate__faster"
            >
                <ac-medium-modal
                    v-if="batchModalItem"
                    class="lg:!top-44 overflow-y-scroll"
                    width="44vw"
                    height="fit-content"
                    title="Stok Ekle (Yeni Parti)"
                    @modal-closed="closeBatchModal"
                >
                    <p class="mb-4 text-xl px-2">
                        <strong>{{ batchModalItem.productName }}</strong> —
                        mevcut miktar: {{ batchModalItem.quantity }} {{ getQuantityLabel(batchModalItem.quantityType) }}
                    </p>
                    <inventory-batch-form
                        :quantity-label="getQuantityLabel(batchModalItem.quantityType)"
                        :default-currency="batchModalItem.purchaseCurrency"
                        :integer-only="requiresWholeUnits(batchModalItem.quantityType)"
                        @batch-submitted="handleBatchSubmitted"
                        @cancel="closeBatchModal"
                    />
                </ac-medium-modal>
            </transition>
        </teleport>

        <!-- Remove stock (FIFO) modal -->
        <teleport to="body">
            <transition
                appear
                type="animation"
                enter-active-class="animate__animated animate__fadeIn animate__fast"
                leave-active-class="animate__animated animate__fadeOut animate__faster"
            >
                <ac-medium-modal
                    v-if="removeModalItem"
                    class="lg:!top-44"
                    width="34vw"
                    height="fit-content"
                    title="Stok Çıkar"
                    @modal-closed="closeRemoveModal"
                >
                    <div class="ac-main-form !border-none">
                        <p class="mb-4 text-xl">
                            <strong>{{ removeModalItem.productName }}</strong> —
                            mevcut miktar: {{ removeModalItem.quantity }} {{ getQuantityLabel(removeModalItem.quantityType) }}
                        </p>
                        <p class="mb-4 text-lg text-gray-500">En eski partiden başlayarak (FIFO) düşülecektir.</p>
                        <div class="ac-form-control w-full">
                            <label for="removeAmount">Çıkarılacak miktar</label>
                            <input id="removeAmount" type="number" min="1" :max="removeModalItem.quantity" v-model="removeModalAmount" @keyup.enter="confirmRemove">
                        </div>
                        <div class="ac-form-control w-full">
                            <label for="removeWorksite">Gönderilen Şantiye</label>
                            <select id="removeWorksite" v-model="removeModalWorksiteId">
                                <option value="">Şantiye Belirtilmemiş</option>
                                <option v-for="worksite in activeWorksites" :key="worksite.id" :value="worksite.id">
                                    {{ worksite.name }}
                                </option>
                            </select>
                        </div>
                        <div class="flex items-center justify-center gap-6 w-full">
                            <button type="button" class="ac-primary-button text-2xl mt-4" @click="confirmRemove">Kaydet</button>
                            <button type="button" class="ac-secondary-button text-2xl mt-4" @click="closeRemoveModal">İptal</button>
                        </div>
                    </div>
                </ac-medium-modal>
            </transition>
        </teleport>
    </main>
</template>

<style lang="scss">

@import "../../styles/global/helpers.scss";
@import "../../styles/global/buttons.scss";
@import "../../styles/global/tables.scss";

</style>

<style scoped>
.sortable-th {
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
}
.sortable-th:hover {
    color: #2563eb;
}
.sort-indicator {
    font-size: 1rem;
    color: #2563eb;
}

.icon-action-button {
    width: 2.4rem;
    height: 2.4rem;
    border-radius: 9999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #d1d5db;
    color: #374151;
    background-color: white;
    transition: all 0.2s;
}
.icon-action-button:hover {
    background-color: #2563eb;
    border-color: #2563eb;
    color: white;
}
.icon-action-button--add:hover {
    background-color: #16a34a;
    border-color: #16a34a;
}
.icon-action-button--remove:hover {
    background-color: #dc2626;
    border-color: #dc2626;
}

.inventory-card {
    background-color: white;
    border-radius: 0.75rem;
    box-shadow: var(--main-box-shadow);
    padding: 1.5rem;
}
.inventory-card__title {
    font-size: 1.8rem;
    font-weight: 600;
    margin-bottom: 1rem;
}
.inventory-card__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 1.4rem;
    padding: 0.4rem 0;
    border-bottom: 1px solid #f3f4f6;
}
.inventory-card__label {
    color: #9ca3af;
}
.inventory-card__actions {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 1.2rem;
}
</style>
